import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { StickyNote, Plus, X, Search, Edit, Trash2, Save, ChevronLeft, GripVertical } from 'lucide-react';
import tokenStore from '../../utils/tokenStore';
import { API_URL } from '../../constants/api';

const getAuthHeaders = () => {
  const token = tokenStore.get();
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
};

const GlobalFloatingNotes = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [view, setView] = useState('list'); // 'list' | 'editor'
  const [editingNote, setEditingNote] = useState(null); // null=new, {id,title,body}=editing
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);

  // Dragging state
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState(() => {
    const saved = localStorage.getItem('floatingNotesPosition');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Validate position is on-screen
        if (parsed.x >= 0 && parsed.x < window.innerWidth - 100 && 
            parsed.y >= 0 && parsed.y < window.innerHeight - 100) {
          return parsed;
        }
      } catch (e) { /* ignore */ }
    }
    // Default safe position
    return { x: Math.max(20, window.innerWidth - 400), y: 100 };
  });
  const dragRef = useRef(null);
  const offsetRef = useRef({ x: 0, y: 0 });

  // Persist position
  useEffect(() => {
    localStorage.setItem('floatingNotesPosition', JSON.stringify(position));
  }, [position]);

  // Drag handlers — dragging is a desktop affordance only; mobile uses a fixed, viewport-clamped layout
  const handleMouseDown = (e) => {
    if (window.innerWidth < 640) return;
    if (e.target.closest('button') || e.target.closest('input') || e.target.closest('textarea')) return;
    setIsDragging(true);
    offsetRef.current = { x: e.clientX - position.x, y: e.clientY - position.y };
    e.preventDefault();
  };

  const handleDoubleClick = () => {
    // Reset to default safe position
    setPosition({ x: Math.max(20, window.innerWidth - 400), y: 100 });
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      const newX = Math.max(0, Math.min(window.innerWidth - 360, e.clientX - offsetRef.current.x));
      const newY = Math.max(0, Math.min(window.innerHeight - 200, e.clientY - offsetRef.current.y));
      setPosition({ x: newX, y: newY });
    };
    const handleMouseUp = () => setIsDragging(false);

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // Fetch notes
  const fetchNotes = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const params = q ? `?search=${encodeURIComponent(q)}` : '';
      const res = await axios.get(`${API_URL}/api/notes${params}`, { headers: getAuthHeaders() });
      const notesData = Array.isArray(res.data) ? res.data : (res.data?.data && Array.isArray(res.data.data) ? res.data.data : []);
      setNotes(notesData);
    } catch (err) {
      console.error('Failed to fetch notes:', err);
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load notes when panel opens
  useEffect(() => {
    if (isOpen) fetchNotes();
  }, [isOpen, fetchNotes]);

  // Sync with FoundersTools when notes are updated from main tool
  useEffect(() => {
    const handleNotesUpdate = () => {
      if (isOpen) {
        fetchNotes();
      }
    };
    window.addEventListener('notesUpdated', handleNotesUpdate);
    return () => window.removeEventListener('notesUpdated', handleNotesUpdate);
  }, [isOpen, fetchNotes]);

  // Allow external triggers (e.g. Today dashboard) to open the panel
  useEffect(() => {
    const handleOpen = () => setIsOpen(true);
    window.addEventListener('openFloatingNotes', handleOpen);
    return () => window.removeEventListener('openFloatingNotes', handleOpen);
  }, []);

  // Search with debounce
  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => fetchNotes(search), 300);
    return () => clearTimeout(t);
  }, [search, isOpen, fetchNotes]);

  // Save note
  const handleSave = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      if (editingNote) {
        const res = await axios.put(`${API_URL}/api/notes/${editingNote.id}`,
          { title, body },
          { headers: getAuthHeaders() }
        );
        setNotes(prev => prev.map(n => n.id === editingNote.id ? res.data : n));
      } else {
        const res = await axios.post(`${API_URL}/api/notes`,
          { title, body },
          { headers: getAuthHeaders() }
        );
        setNotes(prev => [res.data, ...prev]);
      }
      // Notify FoundersTools to sync
      window.dispatchEvent(new CustomEvent('notesUpdated', { detail: { action: editingNote ? 'update' : 'create' } }));
      resetEditor();
    } catch (err) {
      console.error('Failed to save note:', err);
    } finally {
      setSaving(false);
    }
  };

  // Delete note
  const handleDelete = async (noteId) => {
    try {
      await axios.delete(`${API_URL}/api/notes/${noteId}`, { headers: getAuthHeaders() });
      setNotes(prev => prev.filter(n => n.id !== noteId));
      if (editingNote?.id === noteId) resetEditor();
      // Notify FoundersTools to sync
      window.dispatchEvent(new CustomEvent('notesUpdated', { detail: { action: 'delete' } }));
    } catch (err) {
      console.error('Failed to delete note:', err);
    }
  };

  // Open editor
  const openNote = (note) => {
    setEditingNote(note);
    setTitle(note.title);
    setBody(note.body || '');
    setView('editor');
  };

  const openNewNote = () => {
    setEditingNote(null);
    setTitle('');
    setBody('');
    setView('editor');
  };

  const resetEditor = () => {
    setEditingNote(null);
    setTitle('');
    setBody('');
    setView('list');
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Sticky note colors for visual variety
  const noteColors = [
    'from-amber-400/20 to-yellow-300/10 border-amber-500/30',
    'from-pink-400/20 to-rose-300/10 border-pink-500/30',
    'from-sky-400/20 to-blue-300/10 border-sky-500/30',
    'from-lime-400/20 to-green-300/10 border-lime-500/30',
    'from-violet-400/20 to-purple-300/10 border-violet-500/30',
    'from-orange-400/20 to-amber-300/10 border-orange-500/30',
  ];
  const getNoteColor = (idx) => noteColors[idx % noteColors.length];

  // FAB hidden — Notes editor opens only via the `openFloatingNotes` event
  // dispatched from the Today dashboard's Quick notes card. This removes the
  // persistent floating affordance while preserving the editor on demand.
  if (!isOpen) {
    return null;
  }

  // Floating panel when open
  return ReactDOM.createPortal(
    <div
      ref={dragRef}
      style={{
        ...(window.innerWidth >= 640 ? { left: `${position.x}px`, top: `${position.y}px` } : {}),
        cursor: isDragging ? 'grabbing' : 'default',
        backgroundColor: 'var(--card-bg-solid)',
        border: '1px solid rgba(245,158,11,0.15)',
      }}
      data-testid="floating-notes-panel"
      className={`fixed z-[100000] overflow-hidden shadow-2xl shadow-black/30 backdrop-blur-xl flex flex-col transition-transform duration-150 inset-x-2 top-14 bottom-20 rounded-2xl sm:inset-auto sm:top-auto sm:bottom-auto sm:max-h-[520px] sm:w-[340px] sm:rounded-2xl ${isDragging ? 'scale-[1.02]' : ''}`}
    >
      {/* Header - Drag handle */}
      <div
        onMouseDown={handleMouseDown}
        onDoubleClick={handleDoubleClick}
        className="flex items-center justify-between px-4 py-3 border-b select-none"
        style={{ background: 'linear-gradient(to right, rgba(245,158,11,0.08), rgba(234,88,12,0.04))', borderColor: 'rgba(245,158,11,0.15)', cursor: isDragging ? 'grabbing' : 'grab' }}
        title="Drag to move, double-click to reset position"
      >
        <div className="flex items-center gap-2">
          <GripVertical className="w-4 h-4" style={{ color: 'var(--text-disabled)' }} />
          <StickyNote className="w-4 h-4" style={{ color: '#f59e0b' }} />
          <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Quick Notes</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: 'rgba(245,158,11,0.12)', color: '#f59e0b' }}>{notes.length}</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            data-testid="floating-notes-new-btn"
            onClick={openNewNote}
            className="p-2.5 rounded-lg transition-colors"
            style={{ color: '#f59e0b' }}
            title="New Note"
          >
            <Plus className="w-4 h-4" />
          </button>
          <button
            data-testid="floating-notes-close-btn"
            onClick={() => { setIsOpen(false); resetEditor(); }}
            className="p-2.5 rounded-lg transition-colors"
            style={{ color: 'var(--text-muted)' }}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        {view === 'list' ? (
          <div className="p-3 space-y-2">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: 'var(--text-disabled)' }} />
              <input
                data-testid="floating-notes-search"
                type="text"
                placeholder="Search notes..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 rounded-xl text-sm themed-input focus:outline-none transition-colors"
                style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--text-primary)' }}
              />
            </div>

            {/* Notes list */}
            {loading ? (
              <div className="py-8 text-center">
                <div className="animate-spin w-6 h-6 border-2 border-t-transparent rounded-full mx-auto mb-2" style={{ borderColor: '#f59e0b', borderTopColor: 'transparent' }} />
                <p className="text-xs" style={{ color: 'var(--text-disabled)' }}>Loading notes...</p>
              </div>
            ) : notes.length === 0 ? (
              <div className="py-10 text-center">
                <StickyNote className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--text-disabled)' }} />
                <p className="text-sm mb-1" style={{ color: 'var(--text-muted)' }}>{search ? 'No notes match your search' : 'No notes yet'}</p>
                <button
                  onClick={openNewNote}
                  className="text-xs transition-colors" style={{ color: '#f59e0b' }}
                >
                  Create your first note
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {(Array.isArray(notes) ? notes : []).map((note, idx) => (
                  <div
                    key={note.id}
                    data-testid={`floating-note-item-${note.id}`}
                    onClick={() => openNote(note)}
                    className={`group p-3 rounded-xl border bg-gradient-to-br cursor-pointer transition-all duration-200 hover:scale-[1.02] hover:shadow-lg ${getNoteColor(idx)}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="text-sm font-semibold truncate flex-1" style={{ color: 'var(--text-primary)' }}>{note.title}</h4>
                      <button
                        data-testid={`floating-note-delete-${note.id}`}
                        onClick={(e) => { e.stopPropagation(); handleDelete(note.id); }}
                        className="p-2 rounded-md sm:opacity-0 sm:group-hover:opacity-100 hover:bg-red-500/20 hover:text-red-400 active:bg-red-500/20 transition-all"
                        style={{ color: 'var(--text-disabled)' }}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    {note.body && (
                      <p className="text-xs mt-1 line-clamp-2 leading-relaxed" style={{ color: 'var(--text-muted)' }}>{note.body}</p>
                    )}
                    <p className="text-[10px] mt-2" style={{ color: 'var(--text-disabled)' }}>{formatDate(note.updated_at || note.created_at)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* Editor view */
          <div className="p-3 space-y-3">
            <button
              data-testid="floating-notes-back-btn"
              onClick={resetEditor}
              className="flex items-center gap-1 text-xs transition-colors"
              style={{ color: 'var(--text-muted)' }}
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              Back to notes
            </button>
            <input
              data-testid="floating-notes-title-input"
              type="text"
              placeholder="Note title..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl text-sm font-semibold themed-input focus:outline-none transition-colors"
              style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--text-primary)' }}
              autoFocus
            />
            <textarea
              data-testid="floating-notes-body-input"
              placeholder="Write your thoughts..."
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={8}
              className="w-full px-3 py-2.5 rounded-xl text-sm themed-input focus:outline-none resize-none transition-colors"
              style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--text-primary)' }}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={resetEditor}
                className="px-3 py-1.5 text-xs rounded-lg transition-colors"
                style={{ color: 'var(--text-muted)' }}
              >
                Cancel
              </button>
              <button
                data-testid="floating-notes-save-btn"
                onClick={handleSave}
                disabled={!title.trim() || saving}
                className="px-4 py-1.5 bg-amber-500 hover:bg-amber-400 text-slate-900 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                <Save className="w-3.5 h-3.5" />
                {saving ? 'Saving...' : editingNote ? 'Update' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default GlobalFloatingNotes;
